import { Core } from "@faire/design-tokens";
import { getBrandLocationString } from "@faire/retailer-visitor-shared/consts/getBrandLocationString";
import { brandPage } from "@faire/retailer-visitor-shared/paths/index";
import { LoggedOutHomeImage } from "@faire/retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/__internal__/LoggedOutHomeImage/LoggedOutHomeImage";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { logAssert } from "@faire/web--source/common/logging";
import { Carousel } from "@faire/web--source/slate-labs/Carousel/Carousel";
import { Button } from "@faire/web--source/slate/Button";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { cn } from "@faire/web--source/tools/cn";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import { ResponsiveContentV2 } from "@faire/web--source/ui/ResponsiveContentV2";
import { trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick } from "@faire/web-api--source/events/loggedOutRetailers/click/loggedOutHomeRedesignCta";
import { trackLoggedOutRetailersLoggedOutHomeRedesignView } from "@faire/web-api--source/events/loggedOutRetailers/view/loggedOutHomeRedesign";
import { ICategoryBrandsCarousel } from "@faire/web-api--source/indigofair/data/ICategoryBrandsCarousel";
import { ILargeBrandTile } from "@faire/web-api--source/indigofair/data/ILargeBrandTile";
import React from "react";

const BRAND_TILE_WIDTH = 200;
const XLARGE_BRAND_TILE_WIDTH = 260;

const BrandTile: React.FC<{
  brand: ILargeBrandTile;
  pauseScroll?: () => void;
  resumeScroll?: () => void;
}> = ({ brand, pauseScroll, resumeScroll }) => {
  const { strictLocalize } = useStrictLocalization();
  if (!brand?.image) {
    return null;
  }

  const {
    image,
    name,
    based_in,
    description,
    token,
    based_in_city,
    based_in_state,
    based_in_country,
  } = brand;

  logAssert(
    !!token,
    "brand token is required for brand tile to link to brand page"
  );

  const brandLocation =
    getBrandLocationString(
      { city: based_in_city, state: based_in_state, country: based_in_country },
      strictLocalize
    ) ?? based_in;

  return (
    <Link
      to={brandPage(token)}
      className="group"
      onMouseEnter={pauseScroll}
      onMouseLeave={resumeScroll}
      onClick={() => {
        trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick(
          "category_best_brands_banner-brand-tile",
          token ?? "",
          "v1"
        );
      }}
    >
      <Column gap={4} className="h-full w-[200px] no-underline xl:w-[260px]">
        <div className="overflow-hidden">
          <LoggedOutHomeImage
            responsiveImage={{ mobileAndAbove: image }}
            optimizeImageOptions={{
              mobileAndAbove: {
                width: BRAND_TILE_WIDTH,
                height: BRAND_TILE_WIDTH,
              },
              xLargeAndAbove: {
                width: XLARGE_BRAND_TILE_WIDTH,
                height: XLARGE_BRAND_TILE_WIDTH,
              },
            }}
            className="flex origin-left transition-transform duration-[1200ms] ease-[cubic-bezier(0.17,0.67,0.24,1)] group-hover:scale-110"
            altText={name}
          />
        </div>
        <Spacer size={4} />
        <Typography
          variant="paragraphSansMedium"
          maxLines={1}
          className="from-core-text-default to-core-text-default w-fit bg-linear-to-r bg-[length:0%_1px] bg-left-bottom bg-no-repeat transition-all duration-[1200ms] ease-[cubic-bezier(0.17,0.67,0.24,1)] group-hover:bg-[length:100%_1px] group-focus-visible:[outline:2px_solid_var(--core-interactive-focus)_-2px]"
        >
          {name}
        </Typography>
        <Typography
          variant="paragraphSansRegular"
          color={Core.text.subdued}
          maxLines={1}
        >
          {brandLocation}
        </Typography>
        <Typography
          variant="paragraphSansRegular"
          maxLines={2}
          style={{ height: "40px", paddingRight: `${getSpacing("1x")}` }}
        >
          {description}
        </Typography>
      </Column>
    </Link>
  );
};

export const CategoryBestBrandsWithCTA: React.FC<{
  selectedCategory: ICategoryBrandsCarousel;
  pauseScroll?: () => void;
  resumeScroll?: () => void;
  className?: string;
}> = ({ selectedCategory, pauseScroll, resumeScroll, className }) => {
  const carouselRef = useDoActionWhenVisible(() =>
    trackLoggedOutRetailersLoggedOutHomeRedesignView(
      "category_best_brands_banner-category-carousel",
      "v1"
    )
  );
  const brands = selectedCategory.large_brand_tiles;
  return (
    <Column
      className={cn("items-start lg:items-center", className)}
      ref={carouselRef}
    >
      <Spacer height={{ mobileAndAbove: "4x", desktopAndAbove: "3x" }} />
      <ResponsiveContentV2
        desktopAndAbove={
          <Row gap="1x">
            {brands.map((brand, index) => (
              <BrandTile
                brand={brand}
                key={index}
                pauseScroll={pauseScroll}
                resumeScroll={resumeScroll}
              />
            ))}
          </Row>
        }
        tabletAndBelow={
          <Carousel
            items={brands}
            renderItem={(brand) => <BrandTile brand={brand} />}
            freeFlow
            showNavigationArrows={false}
            loop={false}
            noMobilePadding
            onVisible={() => {
              trackLoggedOutRetailersLoggedOutHomeRedesignView(
                "category_best_brands_banner-category-carousel-onscroll",
                "v1"
              );
            }}
          />
        }
      />
      <Spacer height={{ mobileAndAbove: "6x", desktopAndAbove: "5x" }} />
      <Button
        style={{ width: "fit-content" }}
        variant="secondary"
        linkTo={selectedCategory?.category_cta_link}
        className="hover:bg-core-surface-default! bg-transparent!"
        onMouseEnter={pauseScroll}
        onMouseLeave={resumeScroll}
        onClick={() => {
          trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick(
            "category_best_brands_banner-cta",
            selectedCategory?.category_cta_link ?? "",
            "v1"
          );
        }}
      >
        {selectedCategory?.category_cta_link_text}
      </Button>
      <Spacer height={{ mobileAndAbove: "6x", desktopAndAbove: "8x" }} />
    </Column>
  );
};
